import { FC, ReactNode, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { SidebarContext } from 'src/core/contexts/SidebarContext';
import { MenuItems } from 'src/core/types/appConfig';
import { useNavigation, UseNavigationConfig } from 'src/core/hooks/useNavigation';

import Sidebar from './Sidebar';
import Header from './Header';

interface SidebarLayoutProps {
  children?: ReactNode;
  menuItems?: MenuItems[];
  /**
   * Optional configuration for dynamic navigation from EspoCRM
   * If provided, menuItems will be built dynamically from metadata
   */
  navigationConfig?: UseNavigationConfig;
}

const MainWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(
  ({ theme, collapsed }) => `
        flex: 1 1 auto;
        display: flex;
        height: 100%;
        width: 100%;
        
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            margin-left: ${collapsed ? '80px' : theme.sidebar.width};
            transition: margin-left 0.3s ease;
            width: calc(100% - ${collapsed ? '80px' : theme.sidebar.width});
        }
        @media print {
          margin-left: 0 !important;
          width: 100% !important;
        }
`
);

const MainContent = styled(Box)(
  ({ theme }) => `
        margin-top: ${theme.header.height};
        flex: 1 1 auto;
        overflow: auto;
        @media print {
          margin-top: 0 !important;
          overflow: visible !important;
        }
`
);

const SidebarLayout: FC<SidebarLayoutProps> = ({ menuItems: propsMenuItems, navigationConfig }) => {
  const { sidebarCollapsed } = useContext(SidebarContext);

  // Use dynamic navigation if config is provided
  const dynamicNavigation = navigationConfig
    ? useNavigation(navigationConfig)
    : null;

  // Determine which menuItems to use
  // Priority: dynamic navigation > props menuItems
  const menuItems = dynamicNavigation?.menuItems && dynamicNavigation.menuItems.length > 0
    ? dynamicNavigation.menuItems
    : propsMenuItems;

  // Debug logging
  if (navigationConfig) {
    console.log('SidebarLayout - Dynamic Navigation:', {
      isLoading: dynamicNavigation?.isLoading,
      hasError: !!dynamicNavigation?.error,
      menuItemsCount: dynamicNavigation?.menuItems?.length || 0,
      menuItems: dynamicNavigation?.menuItems,
      finalMenuItemsCount: menuItems?.length || 0,
      finalMenuItems: menuItems,
    });
  }

  return (
    <>
      <Sidebar 
        menuItems={menuItems} 
        isLoading={dynamicNavigation?.isLoading}
      />
      <MainWrapper collapsed={sidebarCollapsed}>
        <Header />
        <MainContent>
          <Outlet />
        </MainContent>
      </MainWrapper>
    </>
  );
};

export default SidebarLayout;

